/**
 * Adapted from PlayCanvas Engine's gsplat-work-buffer.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import type * as THREE from 'three'
import type {
  GSplatBufferLeaf,
  GSplatGraphicsAdapter,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import { GSplatFrustumCuller } from '@/plugins/gsplat/unified/GSplatFrustumCuller'
import type { GSplatRendererDataSource } from '@/plugins/gsplat/unified/GSplatRenderer'
import type { GSplatInfo } from '@/plugins/gsplat/unified/world/GSplatInfo'
import type { Disposable } from '@/shared/types/core'

let nextWorkBufferId = 0

export type GSplatWorkBufferPassMode = 'full' | 'colorOnly'

export interface GSplatWorkBufferCopyInput {
  readonly splats: readonly GSplatInfo[]
  readonly camera: THREE.Camera
  readonly changedAllocIds: ReadonlySet<number> | null
}

export interface GSplatWorkBufferCopyStage {
  commit(): void
  rollback(): void
  finalize(): void
}

/** Adapter-specific source pass lease；WorkBuffer 是唯一 owner。 */
export interface GSplatWorkBufferPass extends Disposable {
  readonly mode: GSplatWorkBufferPassMode
  /** 编码/准备 copy，但直到 commit 才提交 GPU side effect。 */
  stage?(input: GSplatWorkBufferCopyInput): GSplatWorkBufferCopyStage
}

export interface GSplatWorkBufferPassFactory {
  create(input: {
    readonly adapter: GSplatGraphicsAdapter
    readonly format: GSplatFormat
    readonly streams: GSplatStreams
    readonly mode: GSplatWorkBufferPassMode
  }): GSplatWorkBufferPass
}

interface StagedWorkBufferStorage {
  readonly streams: GSplatStreams
  readonly orderBuffer: GSplatBufferLeaf
  readonly renderPass: GSplatWorkBufferPass | null
  readonly colorRenderPass: GSplatWorkBufferPass | null
}

/** Params dataFormat transaction 持有的 WorkBuffer storage stage。 */
export interface GSplatWorkBufferFormatStage {
  readonly format: GSplatFormat
  commit(): void
  rollback(): void
  finalize(): void
}

export interface GSplatWorkBufferResizeStage {
  readonly textureSize: number
  commit(): void
  rollback(): void
  finalize(): void
}

export interface GSplatWorkBufferOrderStage {
  commit(): void
  rollback(): void
  finalize(): void
}

/** WorkBuffer 拥有 render-ready streams、source render passes 与 global order GPU leaf。 */
export class GSplatWorkBuffer implements Disposable, GSplatRendererDataSource {
  readonly id = nextWorkBufferId++
  readonly adapter: GSplatGraphicsAdapter
  readonly frustumCuller: GSplatFrustumCuller

  private formatValue: GSplatFormat
  private formatVersionValue: number
  private streamsValue: GSplatStreams
  private orderBufferValue: GSplatBufferLeaf
  private renderPassValue: GSplatWorkBufferPass | null
  private colorRenderPassValue: GSplatWorkBufferPass | null
  private destroyed = false

  constructor(
    adapter: GSplatGraphicsAdapter,
    format: GSplatFormat,
    private readonly passFactory?: GSplatWorkBufferPassFactory
  ) {
    this.adapter = adapter
    this.frustumCuller = new GSplatFrustumCuller(adapter)
    const staged = this.stageStorage(format, 1)
    this.formatValue = format
    this.formatVersionValue = format.extraStreamsVersion
    this.streamsValue = staged.streams
    this.orderBufferValue = staged.orderBuffer
    this.renderPassValue = staged.renderPass
    this.colorRenderPassValue = staged.colorRenderPass
  }

  get format(): GSplatFormat {
    return this.formatValue
  }

  get streams(): GSplatStreams {
    return this.streamsValue
  }

  get orderBuffer(): GSplatBufferLeaf {
    return this.orderBufferValue
  }

  get orderResource(): GSplatBufferLeaf {
    return this.orderBufferValue
  }

  get renderPass(): GSplatWorkBufferPass | null {
    return this.renderPassValue
  }

  get colorRenderPass(): GSplatWorkBufferPass | null {
    return this.colorRenderPassValue
  }

  get textureSize(): number {
    return this.streamsValue.textureDimensions.width
  }

  get capacity(): number {
    return this.textureSize * this.streamsValue.textureDimensions.height
  }

  /** 当前 Manager 独占的 WorkBuffer payload streams 字节数。 */
  get streamGpuBytes(): number {
    let total = 0
    for (const texture of this.streamsValue.getTexturesInOrder()) {
      const bytesPerTexel = this.adapter.getTextureFormatInfo(texture.format).bytesPerTexel
      const byteLength = texture.width * texture.height * bytesPerTexel
      if (!Number.isSafeInteger(byteLength) || byteLength < 0) {
        throw new Error(`非法 GSplat WorkBuffer texture byteLength: ${texture.label}`)
      }
      total += byteLength
      if (!Number.isSafeInteger(total))
        throw new Error('GSplat WorkBuffer GPU bytes 超出安全整数范围')
    }
    return total
  }

  get orderGpuBytes(): number {
    return this.orderBufferValue.byteLength
  }

  get gpuBytes(): number {
    const total = this.streamGpuBytes + this.orderGpuBytes
    if (!Number.isSafeInteger(total))
      throw new Error('GSplat WorkBuffer GPU bytes 超出安全整数范围')
    return total
  }

  getTexture(name: string): GSplatTextureLeaf | undefined {
    this.assertActive()
    return this.streamsValue.getTexture(name)
  }

  setOrderData(data: Uint32Array): void {
    this.assertActive()
    if (data.length > this.capacity) {
      throw new Error(`GSplat order 超出 WorkBuffer 容量：${data.length} > ${this.capacity}`)
    }
    this.adapter.updateBuffer(this.orderBufferValue, data)
  }

  /**
   * CPU sort 结果延迟到 World transaction 的最后提交点再原地上传。
   *
   * PlayCanvas 的 order storage 只在容量增长时更换 identity；普通相机排序必须保持 backing
   * 稳定。Three WebGPU 会把 TSL storage binding 编译到具体 BufferAttribute，逐次 replacement
   * 不仅会触发无谓的 pipeline 重建，还可能让 draw 继续读取旧 binding。
   */
  stageOrderData(data: Uint32Array): GSplatWorkBufferOrderStage {
    this.assertActive()
    if (data.length > this.capacity) {
      throw new Error(`GSplat order 超出 WorkBuffer 容量：${data.length} > ${this.capacity}`)
    }
    let committed = false
    let settled = false
    return {
      commit: () => {
        if (settled) throw new Error('GSplat WorkBuffer order stage 已结束')
        if (committed) return
        this.adapter.updateBuffer(this.orderBufferValue, data)
        committed = true
      },
      rollback: () => {
        if (settled) return
        settled = true
      },
      finalize: () => {
        if (settled) return
        settled = true
      },
    }
  }

  syncWithFormat(format: GSplatFormat = this.formatValue): boolean {
    const stage = this.stageFormat(format)
    if (stage === null) return false
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
    return true
  }

  /** 分配完整 replacement storage，但在 commit 前不改变 render-ready backing。 */
  stageFormat(format: GSplatFormat): GSplatWorkBufferFormatStage | null {
    this.assertActive()
    if (format === this.formatValue && format.extraStreamsVersion === this.formatVersionValue) {
      return null
    }

    const staged = this.stageStorage(format, this.textureSize)
    const previousFormat = this.formatValue
    const previousFormatVersion = this.formatVersionValue
    let previousStorage: StagedWorkBufferStorage | null = null
    let committed = false
    let settled = false

    return {
      format,
      commit: () => {
        if (settled) throw new Error('GSplat WorkBuffer format stage 已结束')
        if (committed) return
        previousStorage = this.detachStorage()
        this.formatValue = format
        this.formatVersionValue = format.extraStreamsVersion
        this.attachStorage(staged)
        committed = true
      },
      rollback: () => {
        if (settled) return
        settled = true
        if (!committed) {
          this.disposeStorage(staged)
          return
        }
        const replacement = this.detachStorage()
        this.formatValue = previousFormat
        this.formatVersionValue = previousFormatVersion
        this.attachStorage(previousStorage!)
        this.disposeStorage(replacement)
      },
      finalize: () => {
        if (settled) return
        settled = true
        if (committed) this.disposeStorage(previousStorage!)
        else this.disposeStorage(staged)
      },
    }
  }

  resize(textureSize: number): void {
    const stage = this.stageResize(textureSize)
    if (!stage) return
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
  }

  /** replacement storage 在 renderer hooks 成功前保持可回滚。 */
  stageResize(textureSize: number): GSplatWorkBufferResizeStage | null {
    this.assertActive()
    if (!Number.isSafeInteger(textureSize) || textureSize < 1) {
      throw new Error(`非法 GSplat WorkBuffer textureSize: ${textureSize}`)
    }
    if (textureSize === this.textureSize) return null

    const staged = this.stageStorage(this.formatValue, textureSize)
    let previous: StagedWorkBufferStorage | null = null
    let committed = false
    let settled = false
    return {
      textureSize,
      commit: () => {
        if (settled) throw new Error('GSplat WorkBuffer resize stage 已结束')
        if (committed) return
        previous = this.detachStorage()
        this.attachStorage(staged)
        committed = true
      },
      rollback: () => {
        if (settled) return
        settled = true
        if (!committed) {
          this.disposeStorage(staged)
          return
        }
        const replacement = this.detachStorage()
        this.attachStorage(previous!)
        this.disposeStorage(replacement)
        committed = false
      },
      finalize: () => {
        if (settled) return
        settled = true
        if (committed) this.disposeStorage(previous!)
        else this.disposeStorage(staged)
      },
    }
  }

  stageCopy(
    mode: GSplatWorkBufferPassMode,
    input: GSplatWorkBufferCopyInput
  ): GSplatWorkBufferCopyStage {
    this.assertActive()
    for (const splat of input.splats) {
      if (splat.activeSplats > 0) splat.ensureSubDrawTexture(this.textureSize)
    }
    const pass = mode === 'full' ? this.renderPassValue : this.colorRenderPassValue
    return (
      pass?.stage?.(input) ?? {
        commit: () => {},
        rollback: () => {},
        finalize: () => {},
      }
    )
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.disposeStorage(this.detachStorage())
    this.frustumCuller.dispose()
  }

  dispose(): void {
    this.destroy()
  }

  private stageStorage(format: GSplatFormat, textureSize: number): StagedWorkBufferStorage {
    const streams = new GSplatStreams(this.adapter)
    let orderBuffer: GSplatBufferLeaf | null = null
    let renderPass: GSplatWorkBufferPass | null = null
    let colorRenderPass: GSplatWorkBufferPass | null = null
    try {
      const capacity = this.preflightStorage(streams, format, textureSize)
      streams.init(format, capacity)
      orderBuffer = this.adapter.createBuffer({
        label: `gsplat-work-buffer-order:${this.id}`,
        byteLength: capacity * Uint32Array.BYTES_PER_ELEMENT,
        usages: Object.freeze(['storage', 'copy-dst', 'indirect'] as const),
      })
      renderPass =
        this.passFactory?.create({
          adapter: this.adapter,
          format,
          streams,
          mode: 'full',
        }) ?? null
      colorRenderPass =
        this.passFactory?.create({
          adapter: this.adapter,
          format,
          streams,
          mode: 'colorOnly',
        }) ?? null
      return { streams, orderBuffer, renderPass, colorRenderPass }
    } catch (error) {
      colorRenderPass?.dispose()
      renderPass?.dispose()
      orderBuffer?.dispose()
      streams.destroy()
      throw error
    }
  }

  /** CPU WorkBuffer 的整套 GPU 布局必须在任一 leaf 分配前通过。 */
  private preflightStorage(
    streams: GSplatStreams,
    format: GSplatFormat,
    textureSize: number
  ): number {
    if (!Number.isSafeInteger(textureSize) || textureSize < 1) {
      throw new Error(`非法 GSplat WorkBuffer textureSize: ${textureSize}`)
    }
    const capacity = textureSize * textureSize
    if (!Number.isSafeInteger(capacity)) {
      throw new Error(`GSplat WorkBuffer capacity 超出安全整数范围: ${textureSize}`)
    }
    const orderBytes = capacity * Uint32Array.BYTES_PER_ELEMENT
    if (!Number.isSafeInteger(orderBytes)) {
      throw new Error(`GSplat WorkBuffer order bytes 超出安全整数范围: ${capacity}`)
    }
    const bufferLimit = this.adapter.limits.maxBufferSize
    if (orderBytes > bufferLimit) {
      throw new Error(
        `GSplat WorkBuffer order 超出 maxBufferSize: actual=${orderBytes}, limit=${bufferLimit}`
      )
    }
    if (this.adapter.backend === 'webgpu') {
      const storageLimit = this.adapter.limits.maxStorageBufferBindingSize
      if (orderBytes > storageLimit) {
        throw new Error(
          `GSplat WorkBuffer order 超出 maxStorageBufferBindingSize: actual=${orderBytes}, limit=${storageLimit}`
        )
      }
    }
    streams.preflight(format, capacity)
    return capacity
  }

  private detachStorage(): StagedWorkBufferStorage {
    return {
      streams: this.streamsValue,
      orderBuffer: this.orderBufferValue,
      renderPass: this.renderPassValue,
      colorRenderPass: this.colorRenderPassValue,
    }
  }

  private attachStorage(storage: StagedWorkBufferStorage): void {
    this.streamsValue = storage.streams
    this.orderBufferValue = storage.orderBuffer
    this.renderPassValue = storage.renderPass
    this.colorRenderPassValue = storage.colorRenderPass
  }

  private disposeStorage(storage: StagedWorkBufferStorage): void {
    storage.colorRenderPass?.dispose()
    storage.renderPass?.dispose()
    storage.orderBuffer.dispose()
    storage.streams.destroy()
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplat WorkBuffer 已销毁')
  }
}
